// Wave F4 NAMEPLATE. The nav tree with merchant vocabulary applied. Nav
// consumers (DesktopSidebar, SideDrawer, SectionTabs, Settings hub) read from
// this instead of importing SECTIONS directly, so a merchant who renames
// "Quotes" to "Estimates" sees it in every nav surface. With the default
// vocabulary the returned tree is byte-identical to SECTIONS.
//
// PLATFORM-1 — and the account's own navigation configuration is applied HERE
// TOO, at the same single hop, so every surface inherits it with no duplicated
// logic: desktop sidebar, side drawer, mobile bottom navigation, sidebar
// search, the command index and the Settings hub grid all read from here.
//
// ON THE ORDER. This applies the account layer BEFORE consumers role-filter,
// because that is where the one hop is. It is safe because BOTH LAYERS ONLY
// SUBTRACT: hiding composes in either order, and reordering and renaming are
// untouched by role filtering. A test asserts the two orders agree on every
// role, so the choice is proven rather than assumed.
//
// THE ADMIN SHELL DOES NOT READ THIS, deliberately - see AdminShell.tsx.

import { useMemo } from 'react'

/**
 * THE-HOME-LOADS-FAST-1 — THE FIFTEEN ICONS A RECORD TYPE MAY CARRY, AND NOT
 * THE OTHER SIXTEEN HUNDRED.
 *
 * FRONTDOOR-SPEED-1 turned `import * as icons from 'lucide-react'` into a
 * dynamic `import('lucide-react')` so a stranger at the door did not download
 * the pack before first paint. That fixed the door and left the dashboard: the
 * effect ran on mount, so **every signed in page fetched 594,900 bytes of icon
 * barrel the moment the menu rendered**, measured at 145.9 KB over the wire on
 * the home's first load.
 *
 * And it was worse than a late download. A dynamic import of the NAMESPACE
 * makes Rollup put all 1,637 icon modules in one chunk, so every ordinary
 * `import { ChevronDown } from 'lucide-react'` anywhere in the shell resolved
 * into that same chunk. One namespace import was holding the whole library in
 * the eager graph of every screen.
 *
 * CAPABILITY IS NOT TRADED, AND THIS TIME IT NEVER WAS. `RECORD_TYPE_ICONS` is
 * a CLOSED LIST OF FIFTEEN and `iconOf` maps anything else to `Box`, so the
 * pack was never reachable past those fifteen names: a merchant could not pick
 * a sixteenth before this wave and cannot after it. What the barrel bought was
 * nothing, and it cost every merchant 146 KB on every first load.
 *
 * Imported by their own paths, which is what lets the rest of the library go.
 */
import Box from 'lucide-react/dist/esm/icons/box'
import Boxes from 'lucide-react/dist/esm/icons/boxes'
import Building2 from 'lucide-react/dist/esm/icons/building-2'
import Car from 'lucide-react/dist/esm/icons/car'
import ClipboardList from 'lucide-react/dist/esm/icons/clipboard-list'
import FileText from 'lucide-react/dist/esm/icons/file-text'
import Folder from 'lucide-react/dist/esm/icons/folder'
import Hammer from 'lucide-react/dist/esm/icons/hammer'
import HeartPulse from 'lucide-react/dist/esm/icons/heart-pulse'
import Key from 'lucide-react/dist/esm/icons/key'
import MapPin from 'lucide-react/dist/esm/icons/map-pin'
import Package from 'lucide-react/dist/esm/icons/package'
import Scale from 'lucide-react/dist/esm/icons/scale'
import Truck from 'lucide-react/dist/esm/icons/truck'
import Wrench from 'lucide-react/dist/esm/icons/wrench'

/** By the name a record type stores, which `iconOf` has already narrowed. */
const RECORD_ICONS: Record<RecordTypeIcon, React.ComponentType<{ className?: string }>> = {
  Box,
  Boxes,
  Building2,
  Car,
  ClipboardList,
  FileText,
  Folder,
  Hammer,
  HeartPulse,
  Key,
  MapPin,
  Package,
  Scale,
  Truck,
  Wrench,
}
import {
  SECTIONS,
  applyVocabularyToSections,
  withRecordTypeSection,
  type NavSection,
} from '../lib/navConfig'
import { withBuiltSection } from '../lib/artifacts/artifactNav'
import { useBuiltThings, thingsForMenu } from './useBuiltThings'
import { useBuiltTabs, tabsForMenu } from './useBuiltTabs'
import { withBuiltTabs } from '../lib/builtTabsNav'
import { resolveAccountNav, type ResolvedNav } from '../lib/navAccountLayer'
import { iconOf, type RecordTypeIcon } from '../lib/recordTypes'
import { useVocabulary } from './useVocabulary'
import { applyMeasureLayer } from '../lib/navMeasureLayer'
import { applySettingsLayer, settingsActivityFrom } from '../lib/settingsWire'
import { useAccountNavConfig } from './useAccountNavConfig'
import { useMenuSignals } from './useMenuSignals'
import { useRecordTypes } from './useRecordTypes'

/**
 * The tree, with the merchant's vocabulary AND their menu configuration.
 *
 * Returns the warning too, so the one surface that should tell a merchant
 * "we could not read your menu settings" can, while the other five just take
 * the sections.
 */
export function useResolvedNav(): ResolvedNav {
  const { vocab } = useVocabulary()
  const account = useAccountNavConfig()
  const signals = useMenuSignals()
  // RECORD-TYPES-1 — a business's own record types join the tree HERE, before
  // the account layer, so PLATFORM-1's hide / rename / reorder applies to them
  // exactly as it does to ours. An account with no types adds no section and
  // the tree is byte-identical to what it was.
  const recordTypes = useRecordTypes()
  // BUILT-THING-IS-A-TAB-1 — the things this business had built join HERE too,
  // at the same hop and for the same reason: before the account layer, so
  // hide, rename and reorder apply to them exactly as they do to ours.
  const built = useBuiltThings()
  // THE-TAB-IS-IN-THE-MENU-1 — tabs Ghost built, from the map, at the same hop.
  const tabs = useBuiltTabs()

  return useMemo(() => {
    const withVocab = applyVocabularyToSections(SECTIONS, vocab)
    const withTypes = withRecordTypeSection(
      withVocab,
      recordTypes.active,
      (name) => RECORD_ICONS[iconOf({ icon: name })],
    )
    // BUILT-THING-IS-A-TAB-1 — THE ONLY ADDITIVE LAYER BESIDES RECORD TYPES,
    // and it is additive rather than an override for a reason worth keeping:
    // `navAccountLayer` may only SUBTRACT so that a merchant override can never
    // add back a node their ROLE was denied. A built thing is not an override,
    // it is a different source, so it joins the tree here and is then subject
    // to every subtractive layer below exactly as our own sections are.
    //
    // A read still in flight or failed contributes NOTHING, so the menu never
    // draws a tab it is not sure of.
    const withBuilt = withBuiltSection(withTypes, thingsForMenu(built), Box)
    // THE-TAB-IS-IN-THE-MENU-1 — A THIRD ADDITIVE SOURCE, FOR THE SAME REASON AS
    // THE SECOND: a built tab is a different source, not an override, so it joins
    // here and every subtractive layer below applies to it. Placed after the
    // Built section and before Settings, in the order the tabs were built.
    const withTabs = withBuiltTabs(withBuilt, tabsForMenu(tabs), Box)
    const resolved = resolveAccountNav(withTabs, account)
    // MENU-WIRE-1 — THE THIRD SUBTRACTIVE LAYER, AT THE SAME ONE HOP.
    //
    // Every surface inherits it because they all read from here: desktop
    // sidebar, side drawer, mobile bottom navigation, sidebar search, the
    // command index and the Settings hub. Wiring it into the sidebar alone
    // would have given a merchant a menu that disagreed with its own search.
    //
    // ORDER DOES NOT MATTER and that is not an assumption: all three layers
    // only REMOVE, which is the same reasoning this file already records for
    // the first two. Signals still loading is an empty object, which removes
    // nothing - the full menu, never a guess at a smaller one.
    const counts = signals.data ?? {}
    const withMeasure = applyMeasureLayer(resolved.sections, counts)
    // SETTINGS-WIRE-1 — the fourth subtractive layer, at the same hop.
    //
    // The settings filter was imported by NOTHING: built, measured on real
    // accounts, proven, never called. It runs after the measure so its module
    // list is the sections that actually survived, which is the honest input -
    // a settings page for a section the account does not have should go with it.
    const measured = {
      ...resolved,
      sections: applySettingsLayer(
        withMeasure,
        settingsActivityFrom(
          counts,
          withMeasure.map((s) => s.id),
        ),
      ),
    }
    // A FAILED TYPE READ IS SURFACED, NOT SWALLOWED. Without this the menu
    // would simply come up short and nothing would say why - which is exactly
    // how a business's own records get hidden from them silently.
    return recordTypes.warning
      ? { ...measured, warning: measured.warning ?? recordTypes.warning }
      : measured
  }, [vocab, account, recordTypes.active, recordTypes.warning, signals.data, built, tabs])
}

export function useNavSections(): NavSection[] {
  return useResolvedNav().sections
}
